'use client';

import { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import CompareBar from './CompareBar';
import CompareInfo from './CompareInfo';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { useLazyGetUniquePartQuery } from '../../../state/uniquePartSlice';
import BlurWrapper from '../../common/BlurWrapper';
import { useSearchParams } from 'react-router-dom';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

interface CompareContainerProps {
    compareItemPns: string[];
    togglePn: (pn: string, checked: boolean) => void;
    clearAll: () => void;
    showCompareInfo: boolean;
    setShowCompareInfo: Dispatch<SetStateAction<boolean>>;
    contentTree: ProductsContent;
}

const CompareContainer: FunctionComponent<CompareContainerProps> = (props) => {
    const [uniqueParts, setUniqueParts] = useState<DetectedUniquePart[]>([]);
    const [trigger] = useLazyGetUniquePartQuery();

    useEffect(() => {
        if (!props.compareItemPns.length) {
            setUniqueParts([]);
            return;
        }
        (async () => {
            try {
                const res = await Promise.all(
                    props.compareItemPns.map((pn) => trigger({ basePn: pn }).unwrap())
                );
                setUniqueParts(res);
            } catch (err) {
                console.error(err);
            }
        })();
    }, [props.compareItemPns, trigger]);

    const [searchParams, setSearchParams] = useSearchParams();

    // URL -> state（單向）
    useEffect(() => {
        const hasDoCompare = searchParams.get('doCompare') === 'true';
        if (hasDoCompare !== props.showCompareInfo) {
            props.setShowCompareInfo(hasDoCompare);
        }
    }, [searchParams, props.showCompareInfo, props.setShowCompareInfo]);

    // 提供給 UI 的兩個 handler：只動 URL
    const handleShowCompare = () => {
        const next = new URLSearchParams(searchParams);
        next.set('doCompare', 'true');
        setSearchParams(next); // 不塞進 history
    };

    const handleCloseCompare = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('doCompare');
        setSearchParams(next);
    };

    return (
        <>
            {props.showCompareInfo ? (
                <BlurWrapper>
                    <CompareInfo
                        compareItems={uniqueParts}
                        handleClose={handleCloseCompare}
                        onClearCompareItem={(pn) => props.togglePn(pn, false)}
                        contentTree={props.contentTree}
                    />
                </BlurWrapper>
            ) : (
                <CompareBar
                    compareItems={uniqueParts}
                    handleClearAllCompareItems={props.clearAll}
                    clearCompareItemByPn={(pn) => props.togglePn(pn, false)}
                    onCompareClick={handleShowCompare}
                    contentTree={props.contentTree}
                />
            )}
        </>
    );
};

export default CompareContainer;
